"use client";

/**
 * CyclingWord — animated cycling identity word for Scene 0.
 *
 * Extracted from inline definition in Scene0MissionOpen on landing LCP refactor
 * (UI-53). Lives as a client overlay over the static RSC Scene0Static base.
 *
 * Behavior:
 *   - Loops through CYCLE_WORDS, fading each word in (400ms), holding 2.1s,
 *     fading out (300ms) → 2.8s/word. Total loop ≈ 67s.
 *   - Mobile variant uses a softer exit (opacity 0.5) so adjacent words remain
 *     legible during transition; desktop fully fades.
 *   - Reduced-motion: static "Founder." anchor word (no loop).
 *
 * a11y: aria-hidden — the H1 in Scene0Static carries the actual meaning.
 *
 * Authoritative spec:
 *   - research/landing-v2/09-wireframe-v2-part-a.md §2 (motion + fallbacks)
 *   - Scene0MissionOpen.tsx S53 comment (dream-selling redesign rationale)
 */

import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useEffect, useState } from "react";

/** Cycling words — 19 identities + "Founder." anchor inserted every 4 words.
 * Identities sorted by (global cohort size × Foundry-buy propensity): high-LTV
 * ICP first (Creator/Seller/Developer) so the first ~8s of the cycle hits the
 * people most likely to convert; long-tail trade identities (Plumber/Mechanic)
 * validate the "everyone" claim later in the loop. The "Founder." anchor — the
 * dream — fires every ~14s (vs once per 56s in the previous shape) so viewers
 * who scroll out early still see it. Loop length: 24 × 2.8s ≈ 67s. */
const CYCLE_WORDS = [
  // Group 1 — high-LTV creators
  "Creator.",
  "Seller.",
  "Developer.",
  "Photographer.",
  "Founder.",
  // Group 2 — knowledge workers
  "Writer.",
  "Designer.",
  "Engineer.",
  "Teacher.",
  "Founder.",
  // Group 3 — services
  "Driver.",
  "Cook.",
  "Coach.",
  "Nurse.",
  "Founder.",
  // Group 4 — crafts
  "Builder.",
  "Farmer.",
  "Stylist.",
  "Therapist.",
  "Founder.",
  // Group 5 — long-tail trades + final anchor
  "Musician.",
  "Mechanic.",
  "Plumber.",
  "Founder.",
] as const;

const APPLE_EASE_OUT = [0.16, 1, 0.3, 1] as const;

/** Cycling-word timing: 400ms fade-in + 2.1s hold + 300ms fade-out = 2.8s/word. */
const CYCLE = {
  fadeIn: 0.4,
  hold: 2.1,
  fadeOut: 0.3,
} as const;
const CYCLE_INTERVAL_MS = (CYCLE.fadeIn + CYCLE.hold + CYCLE.fadeOut) * 1000;

interface CyclingWordProps {
  variant?: "desktop" | "mobile";
}

export function CyclingWord({ variant = "desktop" }: CyclingWordProps) {
  const reduced = useReducedMotion();
  const [index, setIndex] = useState(0);
  const isMobile = variant === "mobile";

  useEffect(() => {
    if (reduced) return;
    const interval = setInterval(() => {
      setIndex((i) => (i + 1) % CYCLE_WORDS.length);
    }, CYCLE_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [reduced]);

  // Reduced-motion: static "Founder." (anchors to mission — everyone is one).
  if (reduced) {
    return (
      <div
        aria-hidden
        className="font-heading italic font-bold tracking-tight leading-[1.05] text-5xl bg-gradient-to-r from-amber-300 via-amber-200 to-white bg-clip-text text-transparent drop-shadow-[0_0_22px_rgba(251,191,36,0.18)] md:text-6xl lg:text-7xl lg:drop-shadow-none xl:text-[80px] 2xl:text-[88px]"
      >
        Founder.
      </div>
    );
  }

  return (
    <div
      aria-hidden
      className="relative h-[1.2em] w-full font-heading italic font-bold tracking-tight leading-[1.05] text-5xl md:text-6xl lg:text-7xl xl:text-[80px] 2xl:text-[88px]"
    >
      <AnimatePresence mode="wait">
        <motion.span
          key={index}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: isMobile ? 0.5 : 0, y: -4 }}
          transition={{
            opacity: { duration: CYCLE.fadeIn, ease: APPLE_EASE_OUT },
            y: { duration: CYCLE.fadeIn, ease: APPLE_EASE_OUT },
          }}
          className="absolute inset-0 block bg-gradient-to-r from-amber-300 via-amber-200 to-white bg-clip-text text-transparent drop-shadow-[0_0_22px_rgba(251,191,36,0.18)] lg:drop-shadow-none"
        >
          {CYCLE_WORDS[index]}
        </motion.span>
      </AnimatePresence>
    </div>
  );
}
